import React, { useEffect } from 'react';

import { FormProvider, useForm } from 'react-hook-form';
import Cookies from 'js-cookie';

import {
  LoadingType,
  PreQualificationStep,
  setAccount,
  setLoadingState,
  setPreQualificationStep,
  setSignInStep,
  SignInStep,
} from 'actions';
import AccountForm, { AccountFormData } from 'components/Form/AccountForm/AccountForm';
import FormHeader from 'components/FormHeader';
import Loader from 'components/Loader';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { identityFullStory } from 'utils/fullStory';
import { BorrowerDto } from 'api/BorrowerApi';
import { FailureNotificationType } from 'notifications';
import * as notificationManager from 'notifications';
import AuthApi from 'api/auth/AuthApi';
import { ErrorDto } from 'errors/ErrorDto';
import { ErrorCodes } from 'enums/ErrorCodes';
import { preQualificationApi } from 'api/PreQualification/PreQualificationApi';
import { AppRoutes } from 'routes/routes.enums';
import useAppNavigate from 'hooks/useAppNavigate';
import { marketingApi } from 'api/MarketingApi';
import { PrequalificationVersion } from 'enums/ApplicationsVariables';
import { CONCEPTUAL_DEVICE_ID_COOKIE } from 'routes/routes.constants';
import { ConceptualHqEvent } from 'enums/ConceptualHqEvent';
import { getConceptualDeviceId } from 'routes/routes.utils';

import styles from './CreateAccount.module.scss';

const CreateAccount = () => {
  const dispatch = useAppDispatch();
  const navigator = useAppNavigate();
  const {
    borrowerId,
    firstName,
    lastName,
    email,
    phone,
    personalIdNumber,
    city,
    state: borrowerState,
    dateOfBirth,
    streetName,
    streetNumber,
    zip,
    county,
    fullConsentAgreed,
  } = useAppSelector((state) => state.borrower);
  const { contractorAutofillUsed } = useAppSelector((state) => state.data);

  const loading = useAppSelector((state) => state.loading[LoadingType.GetData]);
  const handleSubmit = async (data: AccountFormData) => {
    try {
      if ((email || data.email) && data.password && data.password === data.confirmPassword) {
        dispatch(setLoadingState({ type: LoadingType.GetData, loading: true }));

        const userEmail = (email || data.email)!;
        const authApi = new AuthApi();

        const newBorrower = await preQualificationApi.completeBorrowerRegistration({
          firstName: firstName,
          lastName: lastName,
          email: userEmail,
          personalIdNumber: personalIdNumber,
          phone: phone,
          address: {
            streetNumber: streetNumber,
            streetName: streetName,
            city: city,
            stateOrProvince: borrowerState,
            county: county,
            zipOrPostalCode: zip,
          },
          dateOfBirth: dateOfBirth,
          fullConsentAgreed: !!fullConsentAgreed,
          consentAgreed: true,
          deviceId: Cookies.get(CONCEPTUAL_DEVICE_ID_COOKIE),
          contractorAutofillUsed: contractorAutofillUsed,
        });

        if ((newBorrower as ErrorDto)?.error?.errorCode === ErrorCodes.EMAIL_IS_ALREADY_USED) {
          dispatch(setLoadingState({ type: LoadingType.GetData, loading: false }));

          notificationManager.notifyFailure(FailureNotificationType.BorrowerEmailAlreadyExists);

          return;
        }

        if ((newBorrower as ErrorDto)?.error?.errorCode === ErrorCodes.ACTIVE_APPLICATION) {
          dispatch(setLoadingState({ type: LoadingType.GetData, loading: false }));
          navigator(AppRoutes.Prequalification);
          dispatch(setPreQualificationStep(PreQualificationStep.AlreadyHaveActiveApplication));

          return;
        }

        const borrower = newBorrower as BorrowerDto;

        const tokens = await authApi.createAccount(userEmail, data.password, phone, borrower.id);

        localStorage.setItem('token', tokens!.accessToken);
        localStorage.setItem('refreshToken', tokens!.refreshToken);

        identityFullStory(email, email);

        dispatch(setAccount({ email: email }));
        dispatch(setSignInStep(SignInStep.EnterConfirmationCode));
        dispatch(setLoadingState({ type: LoadingType.GetData, loading: false }));
      }
    } catch (error) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      dispatch(setLoadingState({ type: LoadingType.GetData, loading: false }));
    }
  };

  const formMethods = useForm({
    defaultValues: {
      email: email,
      password: '',
      confirmPassword: '',
    },
  });

  useEffect(() => {
    const track = async () => {
      const deviceId = await getConceptualDeviceId();

      if (!deviceId) {
        return;
      }

      marketingApi
        .trackConceptualHqEvent({
          event: ConceptualHqEvent.CreateAccount,
          prequalificationVersion: PrequalificationVersion.First,
          borrowerId: borrowerId,
          deviceId: deviceId,
        })
        .catch(() => {});
    };

    track();
  }, []);

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <div>
          <FormHeader title="Create Account" subtitle="" percent={25} />
          <FormProvider {...formMethods}>
            <AccountForm
              isEmailDisabled
              passwordLabel="Create Password"
              showEmailField
              showPasswordField
              showConfirmPasswordField
              showPasswordCaption
              buttonLabel="Submit"
              onSubmit={handleSubmit}
              buttonRowClassname={styles.buttonRow}
            />
          </FormProvider>
        </div>
      )}
    </>
  );
};

export default CreateAccount;
